import { atualizarPedidoSchema, formatarDuracao } from '@projetive-op/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Ban, History, Pencil } from 'lucide-react'
import { useState } from 'react'

import {
  Formulario,
  RodapeGaveta,
  aoSubmeter,
  useGavetaCadastro,
} from '@/components/cadastros/base'
import { HistoricoFicha } from '@/components/pedidos/historico-ficha'
import { Button } from '@/components/ui/button'
import { Campo, Input, Select, Textarea } from '@/components/ui/campo'
import { EsqueletoLinhas, EstadoVazio } from '@/components/ui/estado'
import { Gaveta } from '@/components/ui/gaveta'
import { Pilula } from '@/components/ui/tabela'
import { toast } from '@/components/ui/toast'
import { cn } from '@/lib/cn'
import { formatarData, paraInputData } from '@/lib/datas'
import { mensagemDeErro } from '@/lib/erros'
import { ROTULO_STATUS, TOM_STATUS, prazoVencido } from '@/lib/pedidos'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/gestao/pedidos/$pedidoId')({ component: DetalhePedido })

const ESTADO: Record<string, { rotulo: string; classe: string }> = {
  BLOQUEADA: { rotulo: 'Aguardando etapa anterior', classe: 'text-fg-subtle' },
  AGUARDANDO: { rotulo: 'Na fila', classe: 'text-fg-muted' },
  EM_PRESET: { rotulo: 'Preparando', classe: 'text-brand' },
  EM_EXECUCAO: { rotulo: 'Em execução', classe: 'text-verde' },
  PAUSADA: { rotulo: 'Pausada', classe: 'text-amarelo' },
  CONCLUIDA: { rotulo: 'Concluída', classe: 'text-verde' },
  CANCELADA: { rotulo: 'Cancelada', classe: 'text-vermelho' },
}

/** Previsto de execução gravado nas etapas do item (as canceladas não contam). */
function previstoDoItem(item: {
  fichas: { etapas: { estado: string; tempoPrevistoSeg: number }[] }[]
}): number {
  return item.fichas
    .flatMap((f) => f.etapas)
    .filter((e) => e.estado !== 'CANCELADA')
    .reduce((s, e) => s + e.tempoPrevistoSeg, 0)
}

/** Etapa com operador no posto: a ficha não é cancelada enquanto isso. */
const EM_ANDAMENTO = new Set(['EM_PRESET', 'EM_EXECUCAO', 'PAUSADA'])

function DetalhePedido() {
  const { pedidoId } = Route.useParams()
  const trpc = useTRPC()
  const qc = useQueryClient()
  const pedido = useQuery({
    ...trpc.pedidos.porId.queryOptions({ id: pedidoId }),
    refetchInterval: 15_000,
  })
  const prioridade = useMutation(
    trpc.pedidos.definirPrioridadeFicha.mutationOptions({
      onSuccess: async () => {
        await qc.invalidateQueries({ queryKey: trpc.pedidos.porId.queryKey({ id: pedidoId }) })
        toast.sucesso('Prioridade da ficha salva.')
      },
      onError: (e) => toast.erro(mensagemDeErro(e)),
    }),
  )
  const origens = useQuery(trpc.cadastros.origens.listar.queryOptions())
  const g = useGavetaCadastro<{ id: string }>()
  const [confirmarPedido, setConfirmarPedido] = useState(false)
  const [confirmarFicha, setConfirmarFicha] = useState<string | null>(null)
  const [historico, setHistorico] = useState<string | null>(null)

  const recarregar = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: trpc.pedidos.porId.queryKey({ id: pedidoId }) }),
      qc.invalidateQueries({ queryKey: trpc.pedidos.listar.queryKey() }),
    ])
  const atualizar = useMutation(
    trpc.pedidos.atualizar.mutationOptions({
      onSuccess: async () => {
        await recarregar()
        g.fechar()
      },
    }),
  )
  const cancelarPedido = useMutation(
    trpc.pedidos.cancelar.mutationOptions({
      onSuccess: async () => {
        await recarregar()
        setConfirmarPedido(false)
        toast.sucesso('Pedido cancelado. As fichas que não começaram saíram da fila.')
      },
      onError: (e) => toast.erro(mensagemDeErro(e)),
    }),
  )
  const cancelarFicha = useMutation(
    trpc.pedidos.cancelarFicha.mutationOptions({
      onSuccess: async () => {
        await recarregar()
        setConfirmarFicha(null)
        toast.sucesso('Ficha cancelada.')
      },
      onError: (e) => toast.erro(mensagemDeErro(e)),
    }),
  )

  if (pedido.data === null || pedido.isError) {
    return (
      <EstadoVazio
        titulo="Pedido não encontrado"
        texto={pedido.isError ? mensagemDeErro(pedido.error) : 'Pode ter sido removido.'}
        tom={pedido.isError ? 'atencao' : 'neutro'}
        acao={
          <Link to="/gestao/pedidos" className="text-sm font-medium text-brand hover:underline">
            Voltar aos pedidos
          </Link>
        }
      />
    )
  }
  const p = pedido.data
  if (!p) return <EsqueletoLinhas linhas={6} className="max-w-2xl" rotulo="Carregando o pedido" />

  const encerrado = p.status === 'CANCELADO' || p.status === 'CONCLUIDO'
  const submit = aoSubmeter({
    schema: atualizarPedidoSchema,
    opcionais: ['referenciaExterna', 'prazo', 'observacao'],
    extra: { id: p.id },
    setErros: g.setErros,
    sucesso: 'Dados do pedido salvos.',
    onValido: (dados) => atualizar.mutateAsync(dados),
  })

  return (
    <>
      <Link
        to="/gestao/pedidos"
        className="mb-4 inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg"
      >
        <ArrowLeft className="size-4" aria-hidden /> Pedidos
      </Link>
      <div className="mb-6 flex flex-wrap items-baseline gap-x-6 gap-y-1">
        <h1 className="text-2xl font-bold">Pedido #{p.numero}</h1>
        <span className="text-fg-muted">{p.origem.nome}</span>
        {p.referenciaExterna && (
          <span className="font-mono text-sm text-fg-muted">{p.referenciaExterna}</span>
        )}
        <span
          className={cn(
            'tabular',
            prazoVencido(p.prazo, p.status) ? 'font-semibold text-vermelho' : 'text-fg-muted',
          )}
        >
          Prazo: {formatarData(p.prazo)}
          {prazoVencido(p.prazo, p.status) && ' · vencido'}
        </span>
        <Pilula tom={TOM_STATUS[p.status] ?? 'neutro'} className="text-sm">
          {ROTULO_STATUS[p.status] ?? p.status}
        </Pilula>
      </div>
      {p.observacao && <p className="mb-6 text-sm text-fg-muted">{p.observacao}</p>}

      {p.status !== 'CANCELADO' && (
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => g.abrirEditar(p)}>
            <Pencil className="size-4" aria-hidden /> Editar dados
          </Button>
          {!encerrado &&
            (confirmarPedido ? (
              <div
                role="alertdialog"
                aria-label="Confirmar cancelamento do pedido"
                className="flex flex-wrap items-center gap-2 rounded-2xl border border-vermelho/40 bg-vermelho/10 px-3 py-1.5 text-sm"
              >
                <span>Cancelar o pedido inteiro? As fichas que não começaram saem da fila.</span>
                <Button
                  variant="vermelho"
                  size="sm"
                  aria-busy={cancelarPedido.isPending}
                  onClick={() => cancelarPedido.mutate({ id: p.id })}
                >
                  Sim, cancelar pedido
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmarPedido(false)}>
                  Voltar
                </Button>
              </div>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => setConfirmarPedido(true)}>
                <Ban className="size-4" aria-hidden /> Cancelar pedido
              </Button>
            ))}
        </div>
      )}

      <div className="flex flex-col gap-8">
        {p.itens.map((item) => (
          <section key={item.id}>
            <h2 className="mb-3 text-lg font-semibold">
              {item.quantidade}× {item.produto.nome}{' '}
              <span className="font-mono text-sm text-fg-subtle">{item.produto.codigo}</span>
              <span className="ml-2 text-sm font-normal text-fg-muted tabular">
                · previsto {formatarDuracao(previstoDoItem(item))}
              </span>
            </h2>
            <div className="grid gap-3 lg:grid-cols-2">
              {item.fichas.map((f) => (
                <article key={f.id} className="card p-4">
                  <header className="mb-3 flex items-center justify-between gap-3">
                    <span className="font-semibold">
                      {f.saldoDe ? `Saldo da ficha ${f.saldoDe.numero}` : `Ficha ${f.numero}`} de{' '}
                      {f.total}{' '}
                      <span className="font-normal text-fg-muted tabular">
                        · {f.quantidade} peças
                      </span>
                    </span>
                    {f.status === 'CONCLUIDA' || f.status === 'CANCELADA' ? (
                      <span className="text-xs font-medium text-fg-muted">
                        {f.status === 'CONCLUIDA' ? 'Concluída' : 'Cancelada'}
                      </span>
                    ) : (
                      <label className="flex items-center gap-2 text-xs text-fg-muted">
                        Prioridade
                        <Input
                          type="number"
                          min={1}
                          className="h-8 w-16 px-2 text-sm"
                          aria-label={`Prioridade manual da ficha ${f.numero}`}
                          defaultValue={f.prioridadeManual ?? ''}
                          onBlur={(ev) => {
                            const v = ev.target.value === '' ? null : Number(ev.target.value)
                            if (v !== f.prioridadeManual)
                              prioridade.mutate({ fichaId: f.id, prioridadeManual: v })
                          }}
                        />
                      </label>
                    )}
                  </header>
                  <ol className="flex flex-col gap-1.5">
                    {f.etapas.map((e) => {
                      const est = ESTADO[e.estado] ?? { rotulo: e.estado, classe: '' }
                      return (
                        <li
                          key={e.id}
                          className="grid grid-cols-[1.25rem_1fr_auto] items-center gap-2 text-sm"
                        >
                          <span className="text-xs text-fg-subtle tabular">{e.ordem}</span>
                          <span>
                            {e.etapa.setor.nome}
                            {e.posto && (
                              <span className="ml-2 font-mono text-xs text-fg-subtle">
                                {e.posto.codigo}
                              </span>
                            )}
                            {e.operador && (
                              <span className="ml-2 text-xs text-fg-muted">{e.operador.nome}</span>
                            )}
                          </span>
                          <span
                            className={cn('text-right text-xs font-medium tabular', est.classe)}
                          >
                            {est.rotulo}
                            {e.estado === 'CONCLUIDA' &&
                              ` · ${e.quantidadeApontada} de ${e.quantidadeEsperada} · ${formatarDuracao(e.tempoExecucaoSeg)} / ${formatarDuracao(e.tempoPrevistoSeg)}`}
                            {e.quantidadePerdida > 0 && (
                              <span className="block text-amarelo">
                                {e.quantidadePerdida} de perda
                                {e.motivoPerda ? ` (${e.motivoPerda.nome})` : ''}
                              </span>
                            )}
                          </span>
                        </li>
                      )
                    })}
                  </ol>
                  <footer className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3 text-sm">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Histórico da ficha ${f.numero} de ${f.total}`}
                      onClick={() => setHistorico(f.id)}
                    >
                      <History className="size-4" aria-hidden /> Histórico
                    </Button>
                    {f.status !== 'CONCLUIDA' &&
                      f.status !== 'CANCELADA' &&
                      !f.etapas.some((e) => EM_ANDAMENTO.has(e.estado)) && (
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          {confirmarFicha === f.id ? (
                            <>
                              <span className="text-fg-muted">
                                Cancelar esta ficha? O que já foi feito fica registrado.
                              </span>
                              <Button
                                variant="vermelho"
                                size="sm"
                                aria-busy={cancelarFicha.isPending}
                                onClick={() => cancelarFicha.mutate({ fichaId: f.id })}
                              >
                                Sim, cancelar ficha
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setConfirmarFicha(null)}
                              >
                                Voltar
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Cancelar a ficha ${f.numero} de ${f.total}`}
                              onClick={() => setConfirmarFicha(f.id)}
                            >
                              <Ban className="size-4" aria-hidden /> Cancelar ficha
                            </Button>
                          )}
                        </div>
                      )}
                  </footer>
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>

      <HistoricoFicha fichaId={historico} onFechar={() => setHistorico(null)} />
      <Gaveta
        aberta={g.aberta}
        titulo={`Editar pedido #${p.numero}`}
        onFechar={g.fechar}
        rodape={
          <RodapeGaveta
            form="form-editar-pedido"
            salvando={atualizar.isPending}
            onCancelar={g.fechar}
          />
        }
      >
        <Formulario
          id="form-editar-pedido"
          onSubmit={submit}
          erros={g.erros}
          onLimparErro={g.limparErro}
        >
          <Campo label="Origem" erro={g.erros.origemId}>
            {(c) => (
              <Select {...c} name="origemId" defaultValue={p.origemId} autoFocus>
                {origens.data
                  ?.filter((o) => o.ativo || o.id === p.origemId)
                  .map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.nome}
                    </option>
                  ))}
              </Select>
            )}
          </Campo>
          <Campo label="Prazo de entrega" erro={g.erros.prazo} dica="Define a ordem da fila.">
            {(c) => <Input {...c} name="prazo" type="date" defaultValue={paraInputData(p.prazo)} />}
          </Campo>
          <Campo
            label="Referência externa"
            erro={g.erros.referenciaExterna}
            dica="Nº do pedido no ERP / marketplace, se houver."
          >
            {(c) => (
              <Input {...c} name="referenciaExterna" defaultValue={p.referenciaExterna ?? ''} />
            )}
          </Campo>
          <Campo label="Observação" erro={g.erros.observacao}>
            {(c) => (
              <Textarea
                {...c}
                name="observacao"
                rows={2}
                className="min-h-20"
                defaultValue={p.observacao ?? ''}
              />
            )}
          </Campo>
          <p className="text-xs text-fg-subtle">
            Itens e quantidades não mudam aqui: cancele a ficha que sobrou ou lance outro pedido.
          </p>
        </Formulario>
      </Gaveta>
    </>
  )
}
